import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DatePicker } from "@/components/DatePicker";
import { Empty, PageHeader } from "@/components/PageHeader";
import { STATUS_STYLE, ShiftLine, TYPE_LABEL, TYPE_STYLE } from "@/pages/admin/Requests";
import { api, errMsg } from "@/lib/api";
import { fmtDay } from "@/lib/dates";

const TimeOffForm = ({ onDone }) => {
  const [f, setF] = useState({ start_date: "", end_date: "", reason: "" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/me/requests", { type: "timeoff", ...f });
      toast.success("Time-off request sent");
      setF({ start_date: "", end_date: "", reason: "" });
      onDone();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <form onSubmit={submit} className="space-y-4 rounded-xl border border-slate-200 bg-white p-6" data-testid="timeoff-form">
      <h2 className="font-display text-lg font-semibold">Request time off</h2>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5"><Label>From</Label><DatePicker value={f.start_date} onChange={(d) => setF({ ...f, start_date: d, end_date: !f.end_date || f.end_date < d ? d : f.end_date })} testId="timeoff-start-picker" /></div>
        <div className="space-y-1.5"><Label>To</Label><DatePicker value={f.end_date} onChange={(d) => setF({ ...f, end_date: d })} minDate={f.start_date || undefined} testId="timeoff-end-picker" /></div>
      </div>
      <div className="space-y-1.5"><Label>Reason (optional)</Label><Textarea value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="timeoff-reason-input" /></div>
      <Button type="submit" className="w-full rounded-full" disabled={!f.start_date || !f.end_date} data-testid="timeoff-submit-btn">Send request</Button>
    </form>
  );
};

export default function MyRequests() {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => {
    api.get("/me/requests").then((r) => setRows(r.data));
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const cancel = async (id) => {
    try {
      await api.delete(`/me/requests/${id}`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <PageHeader title="My requests" subtitle="Holidays, swaps and shift claims" />
      <TimeOffForm onDone={load} />
      {rows.length === 0 ? <Empty testId="my-requests-empty">No requests yet.</Empty> : (
        <div className="space-y-3">
          {rows.map((r) => (
            <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`my-request-${r.id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <Badge className={`${TYPE_STYLE[r.type]} text-white`}>{TYPE_LABEL[r.type]}</Badge>
                <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_STYLE[r.status]}`} data-testid={`my-request-status-${r.id}`}>{r.status}</span>
                {r.status === "pending" && <button onClick={() => cancel(r.id)} className="ml-auto text-xs font-semibold text-rose-600" data-testid={`my-request-cancel-${r.id}`}>Cancel</button>}
              </div>
              <div className="mt-2">
                {r.type === "timeoff" ? <span className="font-mono text-sm">{fmtDay(r.start_date)} → {fmtDay(r.end_date)}</span> : <ShiftLine shift={r.shift} />}
                {r.type === "swap" && <p className="text-sm text-slate-500">Covered by {r.target_staff_name}</p>}
                {r.manager_note && <p className="mt-1 text-xs italic text-slate-500">Manager: {r.manager_note}</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
